// ── La voz de Benito en el cierre del turno ───────────────────────────────────
// Aprobado por el fundador (2026-08-01, ola "más toque de IA", pieza 1): al
// cerrar el día, Benito comenta la jornada en 2 o 3 frases. La inteligencia se
// NOTA en el momento ceremonial que ya existe (CierreTurno), sin popup nuevo.
//
// Seguridad por DIETA de datos, no solo por prompt: al modelo le llegan
// únicamente los hechos agregados y des-identificados de resumenTurno /
// chipsResumenTurno (números y catálogo cerrado; jamás registros crudos, jamás
// texto libre, jamás la cama como identidad). Y solo LOGROS: lo pendiente no
// viaja, así el comentario no puede acusar ni con el mejor prompt del mundo.
//
// La capa `cierre_benito` no está en CAPAS_SONNET → resuelve a Haiku (barato,
// una llamada por cierre y con caché local por día).
import { callClaude } from './ai';
import { chipsResumenTurno } from './registroTurno';

const SYSTEM = `Eres Benito, el compañero de MiPAE, cerrando el turno con un enfermero o enfermera en Chile.
Recibes los hechos agregados de su jornada (números ya calculados). Coméntalos en 2 o 3 frases cálidas y sobrias, como un colega que reconoce el trabajo del día.
Reglas duras:
- Habla SIEMPRE de pacientes, jamás de camas como si fueran personas.
- Usa solo los hechos que recibes: nada de inventar, estimar ni suponer.
- Cero culpa: comenta lo que SÍ se hizo. Nunca menciones lo que falta, ni compares con otros días.
- Sin emojis, sin guiones largos (separa con coma o punto), sin listas: prosa corrida.
- Cierra con una frase corta de descanso o despedida (variada, no siempre la misma).
- Máximo 60 palabras en total.`;

// Exportada para fijar por test la dieta de datos (solo agregados, solo
// logros). Refinada tras la auditoría del 2026-08-01: el DENOMINADOR de las
// evoluciones no viaja salvo que estén todas (con "2 de 5" el modelo podía
// deducir y comentar las 3 que faltan, y la anti-culpa debe ser por
// construcción, no por prompt); la mediana del examen solo viaja cuando es un
// logro (examen temprano), no cuando es un dato comentable en contra.
const EXAMEN_TEMPRANO_MAX_MIN = 60;
export function hechosDe(resumen) {
  const chips = chipsResumenTurno(resumen);
  const hechos = [...chips];
  if (resumen.evolucionesCompletas > 0) {
    hechos.push(resumen.evolucionesCompletas === resumen.totalPacientes
      ? `las ${resumen.evolucionesCompletas} evoluciones del turno completas`
      : `${resumen.evolucionesCompletas} ${resumen.evolucionesCompletas === 1 ? 'evolución completa' : 'evoluciones completas'}`);
  }
  if (Number.isFinite(resumen.examenMinutos) && resumen.examenMinutos <= EXAMEN_TEMPRANO_MAX_MIN) {
    hechos.push(`examen físico a los ${resumen.examenMinutos} minutos de recibir a cada paciente (mediana)`);
  }
  if (resumen.farmacos10ok > 0) {
    hechos.push(`${resumen.farmacos10ok} administración(es) con los 10 correctos verificados`);
  }
  return hechos;
}

// Kill-switch ON por defecto (misma disciplina que toda superficie IA): es
// la única llamada IA auto-disparada de la app (corre al montar el cierre,
// sin toque), así que con mayor razón necesita su interruptor.
export function cierreBenitoHabilitado() {
  return process.env.REACT_APP_CIERRE_BENITO_ENABLED !== 'false';
}

// Caché en localStorage con UNA clave por usuario ({fecha, texto}): "una
// llamada por día" de verdad (sessionStorage era una por pestaña: reabrir la
// app el mismo día re-llamaba, hallazgo del auditor). La clave única no
// acumula días viejos.
const claveCache = (userId) => `mipae:cierre-benito:${userId || 'anon'}`;

// Lee el comentario cacheado del día (una llamada por cierre, no por render).
export function comentarioCierreCacheado(userId, fecha) {
  try {
    const raw = localStorage.getItem(claveCache(userId));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    return obj && obj.fecha === fecha && typeof obj.texto === 'string' ? obj.texto : null;
  } catch { return null; }
}

/**
 * Genera el comentario de Benito para el cierre. Devuelve el texto o lanza
 * Error presentable. Cachea en sessionStorage por usuario+fecha.
 */
export async function comentarioCierreBenito({ resumen, userId, fecha }, signal) {
  const cacheado = comentarioCierreCacheado(userId, fecha);
  if (cacheado) return cacheado;

  const hechos = hechosDe(resumen);
  if (!hechos.length) throw new Error('Sin hechos que comentar.');

  const body = {
    max_tokens: 200,
    system: SYSTEM,
    messages: [{
      role: 'user',
      content: `Hechos de mi turno de hoy (ya agregados, sin identidad de nadie):\n- ${hechos.join('\n- ')}\n\nComéntame el turno.`,
    }],
  };
  const res = await callClaude(body, signal, { capa: 'cierre_benito' });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error('Benito no alcanzó a comentar tu turno.');
  const texto = (data?.content || [])
    .filter((b) => b.type === 'text')
    .map((b) => b.text)
    .join('')
    .trim()
    // La red determinista de las normas: sin guiones largos ni emojis aunque
    // el modelo se olvide (misma disciplina que los pulidos).
    .replace(/—/g, ',')
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '')
    .trim();
  if (!texto) throw new Error('Benito no alcanzó a comentar tu turno.');
  try { localStorage.setItem(claveCache(userId), JSON.stringify({ fecha, texto })); } catch { /* best-effort */ }
  return texto;
}
